/**
 * SSOCompletePage — hit by the backend after a successful WorkOS callback.
 * URL shape: /auth/sso/complete?token=<jwt>
 * We stash the JWT in localStorage (same key as every other login path),
 * then hard-navigate to /dashboard so AuthContext.checkAuth() picks it up
 * and hydrates the user object.
 */
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, ShieldCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const SSOCompletePage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const error = params.get('error');

  useEffect(() => {
    if (error) {
      toast.error(error);
      const t = setTimeout(() => navigate('/login', { replace: true }), 1500);
      return () => clearTimeout(t);
    }
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    localStorage.setItem('kstreak_token', token);
    // Hard reload so AuthContext picks up the new token via checkAuth()
    window.location.replace('/dashboard');
  }, [token, error, navigate]);

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex items-center justify-center p-4" data-testid="sso-complete-page">
      <div className="bg-white rounded-xl p-8 max-w-sm w-full text-center border border-[#E5E7EB]">
        {error ? (
          <>
            <AlertTriangle className="w-10 h-10 text-[#FF3B30] mx-auto mb-3" />
            <p className="text-sm font-medium text-[#0A0A0A]">{error}</p>
            <p className="text-xs text-[#9CA3AF] mt-1">Returning to login…</p>
          </>
        ) : (
          <>
            <ShieldCheck className="w-10 h-10 text-[#002FA7] mx-auto mb-3" />
            <p className="text-sm font-medium text-[#0A0A0A]">Finishing SSO sign-in…</p>
            <Loader2 className="w-5 h-5 animate-spin text-[#002FA7] mx-auto mt-3" />
          </>
        )}
      </div>
    </div>
  );
};

export default SSOCompletePage;
